import { Box, Card, Flex, NativeSelect, Stack, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import {
  OFFSET_HOURS,
  detectOffsetHours,
  formatUtcOffset,
  shiftHistogram,
} from '../../utils/peakHours'
import { useTranslation } from '../../i18n'

// The summary's histogram is bucketed in UTC — one doc serves every admin of the
// event — so the offset lives here, in the browser, and is remembered per event.
const storageKey = (eventId) => `crowds.analytics.tzOffset.${eventId}`

function loadOffset(eventId) {
  try {
    const stored = Number(localStorage.getItem(storageKey(eventId)))
    if (OFFSET_HOURS.includes(stored)) return stored
  } catch {
    // Private windows and blocked site data throw on read; detection still works.
  }
  return detectOffsetHours()
}

export function PeakTimes({ byHourOfDay, eventId }) {
  const { t } = useTranslation()
  const [offset, setOffset] = useState(() => loadOffset(eventId))

  // The page keeps its mount across an event switch, so re-read on eventId.
  useEffect(() => { setOffset(loadOffset(eventId)) }, [eventId])

  const handleChange = (e) => {
    const next = Number(e.target.value)
    setOffset(next)
    try {
      localStorage.setItem(storageKey(eventId), String(next))
    } catch {
      // Not being able to remember the choice is no reason to refuse to apply it.
    }
  }

  const rows = useMemo(() => shiftHistogram(byHourOfDay, offset), [byHourOfDay, offset])

  // Read the tallest bar from the whole set, not from the first row — rows are
  // in clock order, so position no longer implies size.
  const maxCount = rows.reduce((max, { count }) => Math.max(max, count), 1)

  if (rows.length === 0) return null

  return (
    <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
      <Card.Body>
        <Flex align="center" justify="space-between" gap={3} mb={3}>
          <Text fontWeight="semibold">{t('analytics.peakTimesTitle')}</Text>
          <NativeSelect.Root size="xs" width="110px" flexShrink={0}>
            <NativeSelect.Field
              aria-label={t('analytics.timezoneLabel')}
              value={offset}
              onChange={handleChange}
              bg="var(--mc-bg-raised)"
              borderColor="var(--mc-border)"
              color="var(--mc-text)"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="xs"
            >
              {OFFSET_HOURS.map((h) => (
                <option key={h} value={h}>{formatUtcOffset(h)}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Flex>
        <Stack gap={1}>
          {rows.map(({ hour, count }) => (
            <Flex key={hour} align="center" gap={3}>
              <Text fontSize="xs" color="fg.muted" w="48px" flexShrink={0}>
                {t('analytics.hourLabel', { hour: String(hour).padStart(2, '0') })}
              </Text>
              <Box flex="1" minW={0}>
                <Box
                  h="14px"
                  bg="var(--mc-focus)"
                  opacity={0.85}
                  borderRadius="sm"
                  w={`${Math.max(4, (count / maxCount) * 100)}%`}
                />
              </Box>
              <Text fontSize="xs" color="fg" w="40px" flexShrink={0} textAlign="right">
                {count}
              </Text>
            </Flex>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
